import { sendEvent, sendGAEvent } from "~/app/analytics/ga/ga";
import { userIsLoggedIn } from "~/domains/user/utils/roles";

type TrackedAffiliate = Pick<SalesPartnerDTO, "affiliateId" | "name"> | null | undefined;
type TrackedAffiliateName = Pick<SalesPartnerDTO, "name"> | null | undefined;

// "a virtual pageview will be triggered whenever this pop-up appears.
// Virtual page: /virtual-pages/affiliates/{{affiliate name}}"
export const trackAffiliatePopupLoad = (affiliateName: string) => {
  sendEvent({
    event: "virtualPageView",
    virtualPagePath: `/virtual-pages/affiliates/${affiliateName}`,
  });
};

// a datalayer push containing the affiliate partner ID (or better the affiliate name) should be performed on each page load.
export const trackAffiliateOnLoad = (savedAffiliate: TrackedAffiliate, visitedAffiliate: TrackedAffiliate) => {
  const id = savedAffiliate?.affiliateId ?? visitedAffiliate?.affiliateId ?? "";
  const name = savedAffiliate?.name ?? visitedAffiliate?.name ?? "";

  if (id) {
    sendEvent({
      event: "affiliate_data",
      affiliate_id: id,
      affiliate_name: name,
      sales_partner_status: !savedAffiliate
        ? "not saved"
        : userIsLoggedIn()
        ? "saved permanently"
        : "saved temporarily",
    });
  }
};

const trackAffilateEvent = (
  data: { eventLabel: string; push_context: string },
  partner: TrackedAffiliateName,
) => {
  const affiliateName = partner?.name ?? "";
  sendGAEvent({
    eventCategory: "affiliate",
    eventAction: affiliateName,
    ...data,
  });
};

// a datalayer push should be performed for each page load that is containing the affiliation-message
export const trackBannerOnLoad = (partner: TrackedAffiliateName) =>
  trackAffilateEvent(
    { eventLabel: "view", push_context: "page_load_containing_affiliation_msg" },
    partner,
  );

// a datalayer push should be performed whenever a user performs is clicking on the "save"-button.
// With that button clicked, a cookie should be set that prevents the "View" event to be fired any longer
export const trackBannerSave = (partner: TrackedAffiliateName) =>
  trackAffilateEvent(
    { eventLabel: "save", push_context: "aff_msg_save_button_clicked" },
    partner,
  );

// a datalayer push should be performed whenever a user performs is clicking on the "X"-button to close the message.
export const trackBannerClose = (partner: TrackedAffiliateName) =>
  trackAffilateEvent(
    { eventLabel: "close", push_context: "aff_msg_x_close_button_clicked" },
    partner,
  );

// a datalayer push should be performed whenever a user performs is clicking on the "undo"-button to close the message.
// The click on the "undo"-button should also should delete the "save"-cookie
export const trackBannerUndo = (partner: TrackedAffiliateName) =>
  trackAffilateEvent(
    { eventLabel: "undo", push_context: "aff_msg_text_close_button_clicked" },
    partner,
  );

// whenever a user submits this form, a datalayer push should be performed if a user chooses to remember his preferred sales partner.
export const trackMyAccountSavePartner = (name: string) =>
  trackAffilateEvent({ eventLabel: "save", push_context: "remember_sales_partner_on_form_submission" }, { name });

// If a user deselects the automatically pre-selected checkbox "Yes, remember...."
// before submitting the form, the dataLayer push should take place when the form is submitted.	"dataLayer.push({
export const trackMyAccountUnsavePartner = (name: string) =>
  trackAffilateEvent({ eventLabel: "undo", push_context: "deselect_sales_partner_on_form_submission" }, { name });

// a datalayer push should be performed, whenever a user clicks on the "register for free" button	"
export const trackRegisterInAffiliate = (name: string) =>
  trackAffilateEvent({ eventLabel: "register for free", push_context: "register_for_free_button_click" }, { name });

// a datalayer push should be performed, whenever a user clicks on the "request an offer" button
export const trackBannerRequestOfferClick = (partner: TrackedAffiliateName) =>
  trackAffilateEvent(
    { eventLabel: "request an offer", push_context: "request_an_offer_button_click" },
    partner,
  );

// Whenever a user selects the checkbox "my preferred sales partner" at the sales partner finder, a datalayer push should be triggered
export const trackSalesPartnerFinderSelectPreferred = () =>
  sendGAEvent({
    eventCategory: "Sales Partner Finder - Interactions",
    eventAction: "click",
    eventLabel: "Show preferred sales partners",
    push_context: "partner_finder_preferred_sales_partner_selected",
  });

// Whenever a user activates the toggle "default location" below the preferred sales partner entry , a datalayer push should be triggered
export const trackSalesPartnerFinderSelectDefault = (affiliateName: string, zipCode: string, address: string) =>
  sendGAEvent({
    eventCategory: "Sales Partner Finder - Interactions",
    eventAction: "default location",
    eventLabel: affiliateName,
    address: `${zipCode} ${address}`,
    push_context: "sales_partner_finder_default_location_activated",
  });

// Whenever a user selects a subsidiary on my account page and saves it, a datalayer push should be triggered
export const trackMyAccountSaveSubsidiary = (affiliateName: string, zipCode: string, address: string) =>
  sendGAEvent({
    eventCategory: "affiliate",
    eventAction: affiliateName,
    eventLabel: "subsidiary saved",
    address: `${zipCode} ${address}`,
    push_context: "affiliate_subsidiary_saved",
  });
